// src/utils/planLimits.js
import { toMillis } from './time';

/**
 * ✅ Single source of truth for plan limits
 *
 * Canonical fields (from users/{uid}):
 * - plan: "trial" | "monthly" | "yearly" | "free"
 * - trialEndsAt: Firestore Timestamp
 * - studentsLimit: number
 * - aiQuestionsUsed: number (server-controlled)
 *
 * DO NOT use:
 * - planLabel for logic (display only)
 * - "trial_expired" as a plan (derived from trialEndsAt)
 * - subscriptionStatus for logic
 */
export function getPlanLimits(user) {
  const plan = user?.plan || "trial";
  const now = Date.now();
  const trialEndsAtMs = toMillis(user?.trialEndsAt) || 0;

  // ✅ Derive expiry (don't rely on plan === 'trial_expired')
  const isTrial = plan === "trial";
  const expired = isTrial && trialEndsAtMs > 0 && now > trialEndsAtMs;

  // ✅ Use plan (not planLabel) as source of truth
  // ✅ ALIGNED with stripeService.ts STRIPE_PLANS
  // ✅ PR5: Monthly AI caps match backend enforcement
  if (plan === "monthly") {
    return {
      plan: "monthly",
      planLabel: "Monthly Plan",
      expired: false,
      studentsLimit: user?.studentsLimit ?? 50,
      quizLimit: "unlimited",
      quizSaveLimit: "unlimited", // ✅ Paid plans: unlimited quiz saves
      aiQuestionsLimit: 2000, // ✅ PR5: 2000 AI questions/month (matches backend)
      aiQuestionsIsMonthly: true, // ✅ PR5: Monthly cap (resets each month)
      trialEndsAtMs: null
    };
  }

  if (plan === "yearly") {
    return {
      plan: "yearly",
      planLabel: "Yearly Plan",
      expired: false,
      studentsLimit: user?.studentsLimit ?? 50,
      quizLimit: "unlimited",
      quizSaveLimit: "unlimited", // ✅ Paid plans: unlimited quiz saves
      aiQuestionsLimit: 5000, // ✅ PR5: 5000 AI questions/month (matches backend)
      aiQuestionsIsMonthly: true, // ✅ PR5: Monthly cap (resets each month)
      trialEndsAtMs: null
    };
  }

  if (plan === "trial" || plan === "trial_verified") {
    // ✅ Unified trial tier: trial_verified now treated as regular trial
    return {
      plan: "trial",
      planLabel: expired ? "Trial Expired" : "14-Day Trial",
      expired,
      studentsLimit: user?.studentsLimit ?? 10,
      quizLimit: "unlimited",
      quizSaveLimit: 3, // ✅ Trial users: max 3 saved quizzes (upgrade prompt)
      aiQuestionsLimit: 50, // Trial users: 50 AI questions
      trialEndsAtMs
    };
  }

  if (plan === "free") {
    return {
      plan: "free",
      planLabel: "Free (Read-Only)",
      expired: false,
      studentsLimit: 0,
      quizLimit: 0,
      quizSaveLimit: 0, // ✅ Free plan: no quiz saves
      aiQuestionsLimit: 0,
      trialEndsAtMs: null
    };
  }

  // Fallback for unknown plans (treat as trial)
  return {
    plan: "trial",
    planLabel: "14-Day Trial",
    expired: false,
    studentsLimit: 10,
    quizLimit: "unlimited",
    quizSaveLimit: 3, // ✅ Default trial limit
    aiQuestionsLimit: 50,
    trialEndsAtMs: null
  };
}

export function canCreateStudent(user, currentStudentCount = 0) {
  const limits = getPlanLimits(user);
  if (limits.expired) return { ok: false, reason: 'expired', limits };
  if (typeof limits.studentsLimit === 'number' && currentStudentCount >= limits.studentsLimit) {
    return { ok: false, reason: 'limit', limits };
  }
  return { ok: true, reason: null, limits };
}

/**
 * ✅ Check if user can save a quiz to library
 *
 * SECURITY NOTE:
 * - savedQuizCount MUST be incremented server-side only
 * - Client reads are for display/UX only
 * - Actual enforcement happens in Cloud Function
 */
export function canSaveQuiz(user, currentSavedQuizCount = 0) {
  const limits = getPlanLimits(user);
  if (limits.expired) {
    return {
      ok: false,
      reason: 'expired',
      limits,
      message: 'Your trial has expired. Upgrade to save quizzes!'
    };
  }

  // Unlimited plans (paid users)
  if (limits.quizSaveLimit === 'unlimited') {
    return { ok: true, reason: null, limits, remaining: 'unlimited' };
  }

  // Limited plans (trial/free)
  if (typeof limits.quizSaveLimit === 'number') {
    const remaining = limits.quizSaveLimit - currentSavedQuizCount;

    if (remaining <= 0) {
      return {
        ok: false,
        reason: 'quiz_save_limit_reached',
        limits,
        remaining: 0,
        message: `You've saved ${limits.quizSaveLimit} quizzes (trial limit). Upgrade to save unlimited quizzes!`
      };
    }

    return { ok: true, reason: null, limits, remaining };
  }

  // Default: deny if limit is 0 or invalid
  return {
    ok: false,
    reason: 'no_access',
    limits,
    message: 'Upgrade to save quizzes to your library!'
  };
}

/**
 * ✅ Check if user can generate AI questions
 *
 * SECURITY NOTE:
 * - aiQuestionsUsed MUST be incremented server-side only
 * - Client reads are for display/UX only
 * - Actual enforcement happens in Cloud Functions
 *
 * ✅ PR5: Paid plans now have monthly caps (2000/5000 per month)
 */
export function canGenerateAIQuestions(user, questionsToGenerate = 0) {
  const limits = getPlanLimits(user);

  // ✅ PR5: Check if this is a monthly cap (paid plans) or lifetime cap (trial)
  const isMonthly = limits.aiQuestionsIsMonthly === true;

  // Check if user has AI limit
  if (limits.aiQuestionsLimit !== null && typeof limits.aiQuestionsLimit === "number") {
    // ⚠️ WARNING: Client-side counters can be manipulated
    // Server MUST validate this in Cloud Functions (aiUsage/{uid}.usedThisMonth for paid)

    // For monthly caps (paid plans), use aiUsage monthly counter if available
    // For lifetime caps (trial), use aiQuestionsUsed
    const usedQuestions = isMonthly
      ? (user?.aiUsageThisMonth || 0) // Monthly counter (from aiUsage collection)
      : (user?.aiQuestionsUsed || 0);  // Lifetime counter (trial)

    const remaining = limits.aiQuestionsLimit - usedQuestions;

    if (remaining <= 0) {
      const resetMessage = isMonthly
        ? "Resets next month."
        : "Upgrade to continue!";

      return {
        ok: false,
        reason: "ai_limit_reached",
        limits,
        remaining: 0,
        message: `You've used all ${limits.aiQuestionsLimit} AI questions ${isMonthly ? 'this month' : 'in your trial'}. ${resetMessage}`
      };
    }

    if (questionsToGenerate > remaining) {
      const resetMessage = isMonthly
        ? "Resets next month."
        : "Please reduce your quiz size or upgrade!";

      return {
        ok: false,
        reason: "ai_limit_exceeded",
        limits,
        remaining,
        message: `You only have ${remaining} AI questions remaining ${isMonthly ? 'this month' : 'in your trial'}. ${resetMessage}`
      };
    }

    return { ok: true, reason: null, limits, remaining };
  }

  // aiQuestionsLimit === null means unlimited
  return { ok: true, reason: null, limits, remaining: "unlimited" };
}